/**
 * isSubmittedMyEvent — ownership predicate for routing a payload between
 * `createMyEvent`/`updateMyEvent`/`cancelMyEvent` (own) and
 * `createEvent`/`updateEvent`/`cancelEvent` (anothers).
 *
 * Two ownership paths:
 *   1. Pilot: any of `pilotID` / `pilotId` / `payPersonID` / `payPersonId`
 *      matches `currentUserId`. The capital-ID variants come from
 *      `buildEventPayload` (legacy parity), the camelCase variants come from
 *      the normalized event-detail DTO (`normalizeEventDetail` in
 *      `EventViewModal.js`). Predicate must tolerate BOTH or routing breaks
 *      (Rem20 Sub 7a — Cancel was routing own events to `/cancel` → 403).
 *      Mirrors `eventPermissions.isOwnEvent`.
 *   2. Instructor: the instructor field stores a *resource* id, NOT a user
 *      id. Look up the resource in the bridged `resources` map and compare
 *      its underlying `userID` to `currentUserId`. Comparing the resource
 *      id directly to user id is wrong (verified during sub-epic round 3).
 *      Accepts both `instructorID` and `instructorId` for the same reason.
 *
 * @param {Object} payload  Result of `buildEventPayload` OR a normalized event detail.
 * @param {number|string} currentUserId
 * @param {Object} resources  Redux `state.resources` map keyed by resource id.
 * @returns {boolean}
 */
export function isSubmittedMyEvent(payload, currentUserId, resources) {
	if (!payload || currentUserId == null) return false;
	const eq = (a, b) => a != null && b != null && String(a) === String(b);

	const pilot =
		payload.pilotID ??
		payload.pilotId ??
		payload.payPersonID ??
		payload.payPersonId ??
		null;
	if (eq(pilot, currentUserId)) return true;

	const instructorResourceId = payload.instructorID ?? payload.instructorId ?? null;
	if (instructorResourceId != null) {
		const resource = resources && (resources[instructorResourceId] ?? resources[String(instructorResourceId)]);
		if (resource) {
			const resourceUserId =
				resource.userID ?? resource.userId ?? resource._userID ?? null;
			if (eq(resourceUserId, currentUserId)) return true;
		}
	}

	return false;
}
